import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitFormData } from '../../../pages/api';

const Form_15 = () => {
    const navigate = useNavigate();

    const initialLineData = {
        sn: '',
        longSide: '',
        shortSideTop: '',
        shortSideMiddle: '',
        shortSideBottom: '',
        height: '',
        backLong: '',
        backShort: '',
        coatingThickness: '',
        deviationDiagonal: '',
        noDrainHoles: '',
        noGroundHoles: '',
        groundingMark: '',
        positiveCable: '',
        negativeCable: '',
        yPitch1400: '',
        yPitch1000: '',
        yPitch400: '',
        xPitchTopHole: '',
        xPitchMiddleHole: '',
        xPitchBottomHole: '',
        hole1Length: '',
        hole1Width: '',
        hole2Length: '',
        hole2Width: '',
        hole3Length: '',
        hole3Width: '',
        result: '',
        remarks: '',
    };

    const [lineOptions, setLineOptions] = useState([]);
    const [formData, setFormData] = useState({
        date: sessionStorage.getItem('inspectionDate') || '',
        lines: {},
    });
    const [selectedLine, setSelectedLine] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [metadataValues, setMetadataValues] = useState({});

    // Initialize line options, form data, and metadata
    useEffect(() => {
        const metadata = sessionStorage.getItem('metadata');
        if (metadata) {
            try {
                const parsedMetadata = JSON.parse(metadata);
                const lines = parsedMetadata.lines?.map(line => line.Lines) || [];
                setLineOptions(lines);

                // Initialize formData.lines
                setFormData(prevData => {
                    const existingLines = Object.keys(prevData.lines);
                    const newLines = lines.reduce((acc, line) => {
                        if (!existingLines.includes(line)) {
                            acc[line] = { ...initialLineData };
                        }
                        return acc;
                    }, {});

                    return {
                        ...prevData,
                        lines: {
                            ...prevData.lines,
                            ...newLines
                        }
                    };
                });
                setSelectedLine(lines[0] || '');

                // Parse moduleDimensionCheck from metadata
                const moduleDimensionCheck = parsedMetadata.moduleDimensionCheck || [];
                const metadataByLine = moduleDimensionCheck.reduce((acc, item) => {
                    acc[item.line] = item;
                    return acc;
                }, {});
                setMetadataValues(metadataByLine);
            } catch (error) {
                console.error('Error parsing metadata:', error);
            }
        }
    }, []);

    // Prefill from inspectionDate
    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        if (inspectionDate) {
            setFormData((prevData) => ({
                ...prevData,
                date: inspectionDate,
            }));
        }
    }, []);

    // Prefill data, prioritizing inspectionData
    useEffect(() => {
        if (lineOptions.length === 0 || !selectedLine) return;

        let updatedData = { ...formData };

        // Helper function to check if a line has any non-empty fields
        const hasNonEmptyFields = (lineData) => {
            return Object.values(lineData || {}).some(value => value !== '');
        };

        // First, try to load from inspectionData
        const inspectionData = sessionStorage.getItem('inspectionData');
        if (inspectionData) {
            try {
                const parsedInspectionData = JSON.parse(inspectionData);
                const moduleData = parsedInspectionData.ModuleDimensionCheck || {};

                if (moduleData && Object.keys(moduleData).length > 0) {
                    updatedData = {
                        ...updatedData,
                        date: moduleData.date || updatedData.date,
                        lines: lineOptions.reduce((acc, line) => ({
                            ...acc,
                            [line]: {
                                ...initialLineData,
                                ...moduleData.lines?.[line]
                            }
                        }), {}),
                    };
                }
            } catch (error) {
                console.error('Error parsing inspectionData:', error);
            }
        }

        // Fallback to saved form data only if inspectionData is absent or selected line is empty
        const hasInspectionData = inspectionData && hasNonEmptyFields(updatedData.lines[selectedLine]);
        if (!hasInspectionData) {
            const savedFormData = sessionStorage.getItem('ModuleDimensionCheckF15formData');
            if (savedFormData) {
                try {
                    const parsedFormData = JSON.parse(savedFormData);
                    updatedData = {
                        ...updatedData,
                        date: parsedFormData.date || updatedData.date,
                        lines: lineOptions.reduce((acc, line) => ({
                            ...acc,
                            [line]: {
                                ...initialLineData,
                                ...parsedFormData.lines?.[line]
                            }
                        }), {}),
                    };
                } catch (error) {
                    console.error('Error parsing saved form data:', error);
                }
            }
        }

        setFormData(updatedData);
    }, [lineOptions, selectedLine]);

    useEffect(() => {
        sessionStorage.setItem('ModuleDimensionCheckF15formData', JSON.stringify(formData));
    }, [formData]);

    const handleDateChange = (value) => {
        setFormData((prevData) => ({ ...prevData, date: value }));
    };

    const handleLineChange = (e) => {
        setSelectedLine(e.target.value);
    };

    const handleFieldChange = (field, value) => {
        setFormData((prevData) => ({
            ...prevData,
            lines: {
                ...prevData.lines,
                [selectedLine]: {
                    ...prevData.lines[selectedLine],
                    [field]: value
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            const payload = {
                projectId: localStorage.getItem('selectedProject'),
                inspectionSupplier: sessionStorage.getItem('inspectionSupplier'),
                inspectionDate: formData.date,
                inspectionShift: sessionStorage.getItem('inspectionShift'),
                inspectorName: sessionStorage.getItem('username'),
                status: 'completed',
                ModuleDimensionCheck: {
                    date: formData.date,
                    lines: formData.lines
                }
            };

            await submitFormData(payload);
            alert('Form submitted successfully!');
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(`Failed to submit form: ${error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNavigation = (target) => {
        navigate(target);
    };

    const renderFormFields = () => {
        if (!formData.lines || !formData.lines[selectedLine]) {
            return <div>Error: Form data not properly initialized</div>;
        }

        const currentLineData = formData.lines[selectedLine];
        const currentMetadata = metadataValues[selectedLine] || {};

        return (
            <>
                <h4 className="font-semibold text-gray-700">Module Dimensions</h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                        <label>Long Side ({currentMetadata.longSide || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.longSide || ''}
                            onChange={(e) => handleFieldChange('longSide', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>Short Side ({currentMetadata.shortSideTop || 'N/A'} mm) - Top</label>
                        <input
                            type="number"
                            value={currentLineData.shortSideTop || ''}
                            onChange={(e) => handleFieldChange('shortSideTop', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>Short Side ({currentMetadata.shortSideMiddle || 'N/A'} mm) - Middle</label>
                        <input
                            type="number"
                            value={currentLineData.shortSideMiddle || ''}
                            onChange={(e) => handleFieldChange('shortSideMiddle', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>Short Side ({currentMetadata.shortSideBottom || 'N/A'} mm) - Bottom</label>
                        <input
                            type="number"
                            value={currentLineData.shortSideBottom || ''}
                            onChange={(e) => handleFieldChange('shortSideBottom', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>Height ({currentMetadata.height || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.height || ''}
                            onChange={(e) => handleFieldChange('height', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                </div>

                <h4 className="font-semibold text-gray-700 mt-6">Frame Width</h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                        <label>Back - Long ({currentMetadata.FWBackLong || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.backLong || ''}
                            onChange={(e) => handleFieldChange('backLong', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>Back - Short ({currentMetadata.FWBackShort || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.backShort || ''}
                            onChange={(e) => handleFieldChange('backShort', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>Coating Thickness ≥ {currentMetadata.coatingThickness || 'N/A'} µm</label>
                        <input
                            type="number"
                            value={currentLineData.coatingThickness || ''}
                            onChange={(e) => handleFieldChange('coatingThickness', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                        <label>Deviation of Diagonal (mm) ≤ {currentMetadata.dOD || 'N/A'} mm</label>
                        <input
                            type="number"
                            value={currentLineData.deviationDiagonal || ''}
                            onChange={(e) => handleFieldChange('deviationDiagonal', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>No Drain Holes</label>
                        <select
                            value={currentLineData.noDrainHoles || ''}
                            onChange={(e) => handleFieldChange('noDrainHoles', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        >
                            <option value="">Select</option>
                            <option value="Ok">Ok</option>
                            <option value="Not Ok">Not Ok</option>
                        </select>
                    </div>
                    <div>
                        <label>No of Ground Holes</label>
                        <select
                            value={currentLineData.noGroundHoles || ''}
                            onChange={(e) => handleFieldChange('noGroundHoles', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        >
                            <option value="">Select</option>
                            <option value="Ok">Ok</option>
                            <option value="Not Ok">Not Ok</option>
                        </select>
                    </div>
                    <div>
                        <label>Grounding Mark</label>
                        <select
                            value={currentLineData.groundingMark || ''}
                            onChange={(e) => handleFieldChange('groundingMark', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        >
                            <option value="">Select</option>
                            <option value="Ok">Ok</option>
                            <option value="Not Ok">Not Ok</option>
                        </select>
                    </div>
                </div>

                <h4 className="font-semibold text-gray-700 mt-6">Length of Cable</h4>
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                        <label>+ve ({currentMetadata['LenghtOfCable+ve'] || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.positiveCable || ''}
                            onChange={(e) => handleFieldChange('positiveCable', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>-ve ({currentMetadata['LenghtOfCable-ve'] || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.negativeCable || ''}
                            onChange={(e) => handleFieldChange('negativeCable', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                </div>

                <h4 className="font-semibold text-gray-700 mt-6">Y Pitch (Hole to Hole Distance)</h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                        <label>({currentMetadata.yPitchLeft || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.yPitch1400 || ''}
                            onChange={(e) => handleFieldChange('yPitch1400', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>({currentMetadata.YPitchMiddle || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.yPitch1000 || ''}
                            onChange={(e) => handleFieldChange('yPitch1000', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>({currentMetadata.YPitchRight || 'N/A'} mm)</label>
                        <input
                            type="number"
                            value={currentLineData.yPitch400 || ''}
                            onChange={(e) => handleFieldChange('yPitch400', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                </div>

                <h4 className="font-semibold text-gray-700 mt-6">X Pitch (Hole to Hole Distance)</h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                        <label>Top Hole ({currentMetadata.XPitchTopHole || 'N/A'})</label>
                        <input
                            type="number"
                            value={currentLineData.xPitchTopHole || ''}
                            onChange={(e) => handleFieldChange('xPitchTopHole', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>Middle Hole ({currentMetadata.XPitchMiddleHole || 'N/A'})</label>
                        <input
                            type="number"
                            value={currentLineData.xPitchMiddleHole || ''}
                            onChange={(e) => handleFieldChange('xPitchMiddleHole', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                    <div>
                        <label>Bottom Hole ({currentMetadata.XPitchBottomHole || 'N/A'})</label>
                        <input
                            type="number"
                            value={currentLineData.xPitchBottomHole || ''}
                            onChange={(e) => handleFieldChange('xPitchBottomHole', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                </div>

                <h4 className="font-semibold text-gray-700 mt-6">Aperture Size (mm)</h4>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                        <label>Hole 1 ({currentMetadata.apertureSizeHole1Lenght || 'N/A'} x {currentMetadata.apertureSizeHole1Width || 'N/A'})</label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                placeholder="Length"
                                value={currentLineData.hole1Length || ''}
                                onChange={(e) => handleFieldChange('hole1Length', e.target.value)}
                                className="w-full border border-gray-300 p-2 rounded-lg"
                            />
                            <input
                                type="number"
                                placeholder="Width"
                                value={currentLineData.hole1Width || ''}
                                onChange={(e) => handleFieldChange('hole1Width', e.target.value)}
                                className="w-full border border-gray-300 p-2 rounded-lg"
                            />
                        </div>
                    </div>
                    <div>
                        <label>Hole 2 ({currentMetadata.apertureSizeHole2Lenght || 'N/A'} x {currentMetadata.apertureSizeHole2Width || 'N/A'})</label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                placeholder="Length"
                                value={currentLineData.hole2Length || ''}
                                onChange={(e) => handleFieldChange('hole2Length', e.target.value)}
                                className="w-full border border-gray-300 p-2 rounded-lg"
                            />
                            <input
                                type="number"
                                placeholder="Width"
                                value={currentLineData.hole2Width || ''}
                                onChange={(e) => handleFieldChange('hole2Width', e.target.value)}
                                className="w-full border border-gray-300 p-2 rounded-lg"
                            />
                        </div>
                    </div>
                    <div>
                        <label>Hole 3 ({currentMetadata.apertureSizeHole3Lenght || 'N/A'} x {currentMetadata.apertureSizeHole3Width || 'N/A'})</label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                placeholder="Length"
                                value={currentLineData.hole3Length || ''}
                                onChange={(e) => handleFieldChange('hole3Length', e.target.value)}
                                className="w-full border border-gray-300 p-2 rounded-lg"
                            />
                            <input
                                type="number"
                                placeholder="Width"
                                value={currentLineData.hole3Width || ''}
                                onChange={(e) => handleFieldChange('hole3Width', e.target.value)}
                                className="w-full border border-gray-300 p-2 rounded-lg"
                            />
                        </div>
                    </div>
                </div>

                <h4 className="font-semibold text-gray-700 mt-6">Results & Remarks</h4>
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                        <label>Result</label>
                        <select
                            value={currentLineData.result || ''}
                            onChange={(e) => handleFieldChange('result', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        >
                            <option value="">Select</option>
                            <option value="Ok">Ok</option>
                            <option value="Not Ok">Not Ok</option>
                        </select>
                    </div>
                    <div>
                        <label>Remarks</label>
                        <input
                            type="text"
                            value={currentLineData.remarks || ''}
                            onChange={(e) => handleFieldChange('remarks', e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded-lg"
                        />
                    </div>
                </div>
            </>
        );
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Module Dimensions Check</h3>
            <p className="text-gray-600 mb-4">Frequency: 1 Module per Every hour</p>

            <div className="grid grid-cols-3 gap-4 w-full mb-6">
                <div>
                    <label>Date</label>
                    <input
                        type="date"
                        value={formData.date || ''}
                        readOnly
                        onChange={(e) => handleDateChange(e.target.value)}
                        className="w-full border border-gray-300 p-2 rounded-lg"
                    />
                </div>
                <div>
                    <label>Line</label>
                    <select
                        value={selectedLine || ''}
                        onChange={handleLineChange}
                        className="w-full border border-gray-300 p-2 rounded-lg"
                    >
                        <option value="">Select a line</option>
                        {lineOptions.map(line => (
                            <option key={line} value={line}>{line}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label>SN.</label>
                    <input
                        type="text"
                        value={formData.lines[selectedLine]?.sn || ''}
                        onChange={(e) => handleFieldChange('sn', e.target.value)}
                        className="w-full border border-gray-300 p-2 rounded-lg"
                        disabled={!selectedLine}
                    />
                </div>
            </div>

            {selectedLine ? renderFormFields() : <div>Please select a line to view the form.</div>}

            <div className="flex justify-between mt-4">
                <button
                    type="button"
                    onClick={() => handleNavigation('/inspections/section2/form14')}
                    className="bg-gray-500 text-white py-2 px-4 rounded"
                >
                    Previous
                </button>
                <button
                    type="submit"
                    disabled={isLoading || !selectedLine}
                    className="bg-[#424752] text-white py-2 px-4 rounded disabled:opacity-50"
                >
                    {isLoading ? 'Submitting...' : 'Submit'}
                </button>
                <button
                    type="button"
                    onClick={() => handleNavigation('/inspections/section2/form16')}
                    className="bg-gray-500 text-white py-2 px-4 rounded"
                >
                    Next
                </button>
            </div>
        </form>
    );
};

export default Form_15;